import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_HR_SECTIONS } from '../utils/hrMaterials';

/**
 * Department template structures (section headers + which templates live in
 * each) are editable from the CRM collections pages (HR Collections, Sales
 * Materials) and stored one doc per department under
 * templatesStructures/<structureId>. Icons are NOT stored — they're mapped
 * client-side (see utils/hrMaterials for HR).
 */
const structureRef = (structureId) => doc(db, 'templatesStructures', structureId);

const toPlainSections = (sections) =>
  sections.map(({ id, label, emoji: sectionEmoji, items }) => ({
    id,
    label,
    emoji: sectionEmoji || '',
    items: (items || []).map(({ title, name, description, emoji }) => ({
      title,
      name: name || title,
      description: description || '',
      emoji: emoji || '',
    })),
  }));

/** Fetch a department's editable structure, falling back to the given defaults. */
export const fetchTemplateStructure = async (structureId, defaultSections = []) => {
  try {
    const snap = await getDoc(structureRef(structureId));
    if (snap.exists()) {
      const sections = snap.data().sections;
      if (Array.isArray(sections) && sections.length > 0) {
        return toPlainSections(sections);
      }
    }
  } catch (err) {
    console.error(`Error fetching ${structureId} structure, using defaults:`, err);
  }
  return toPlainSections(defaultSections);
};

/** Persist a department's full structure (sections + items). */
export const saveTemplateStructure = async (structureId, sections) => {
  await setDoc(
    structureRef(structureId),
    { sections: toPlainSections(sections), updatedAt: new Date().toISOString() },
    { merge: true }
  );
};

/** Fetch the editable HR structure, falling back to the built-in defaults. */
export const fetchHRStructure = () => fetchTemplateStructure('hr', DEFAULT_HR_SECTIONS);

/** Persist the full HR structure (sections + items). */
export const saveHRStructure = (sections) => saveTemplateStructure('hr', sections);

/** Generate a unique, stable section id from its label. */
export const makeSectionId = (label, existingSections = []) => {
  const base =
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || 'section';
  const taken = new Set(existingSections.map((s) => s.id));
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}-${i}`)) i += 1;
  return `${base}-${i}`;
};
